import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, TrendingUp, Landmark, Award, Layers, PiggyBank, ShieldCheck } from "lucide-react";
import { PublicNav, PublicFooter, PublicHero } from "@/components/public/PublicChrome";
import api from "@/lib/api";
import { Loading } from "@/pages/_shared";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  Loan: { icon: Landmark, accent: "#1a5fd0" },
  "Line of Credit": { icon: Layers, accent: "#059669" },
  Grant: { icon: Award, accent: "#b0862f" },
  Tradeline: { icon: TrendingUp, accent: "#7c3aed" },
  Equity: { icon: PiggyBank, accent: "#dc2626" },
};

const DIFFICULTY_TONE = {
  Beginner: "bg-emerald-50 text-emerald-600",
  Intermediate: "bg-amber-50 text-amber-600",
  Advanced: "bg-rose-50 text-rose-600",
};

export default function Opportunities() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("All");
  const primaryTo = user ? "/funding-readiness" : "/register";

  useEffect(() => {
    let ok = true;
    api.get("/opportunities", { params: active === "All" ? {} : { type: active } })
      .then((r) => { if (ok) setData(r.data); })
      .catch(() => { if (ok) setData({ opportunities: [], types: {} }); });
    return () => { ok = false; };
  }, [active]);

  const types = ["All", ...Object.keys(data?.types || {})];

  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="opportunities-page">
      <PublicNav />
      <PublicHero
        eyebrow="Opportunities"
        title="Capital paths for"
        accent="every stage."
        sub="A curated directory of the funding types that grow businesses — what each one is, who it fits, and what you need to qualify. Educational, not an offer of credit."
      />
      <div className="mx-auto max-w-7xl px-5 pb-16 pt-8 lg:px-8">
        <div className="flex flex-wrap gap-2.5" data-testid="opportunities-types">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setActive(t)}
              data-testid={`opp-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                active === t ? "bg-[#0f172a] text-[#d8b25a]" : "border border-slate-300 bg-white text-slate-600 hover:border-slate-400"
              }`}
            >
              {t}{t !== "All" && data?.types?.[t] ? ` · ${data.types[t]}` : ""}
            </button>
          ))}
        </div>

        {!data ? (
          <Loading />
        ) : data.opportunities.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center text-[14px] text-slate-500">No opportunities in this category yet.</div>
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {data.opportunities.map((o) => {
              const meta = TYPE_META[o.type] || { icon: Landmark, accent: "#1a5fd0" };
              const Icon = meta.icon;
              return (
                <div key={o.slug} data-testid={`opportunity-${o.slug}`} className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${meta.accent}15` }}>
                      <Icon size={20} style={{ color: meta.accent }} />
                    </span>
                    {o.difficulty ? <span className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-semibold ${DIFFICULTY_TONE[o.difficulty] || "bg-slate-100 text-slate-500"}`}>{o.difficulty}</span> : null}
                  </div>
                  <div className="mt-3 font-mono text-[9px] font-bold uppercase tracking-wider" style={{ color: meta.accent }}>{o.type}</div>
                  <h3 className="mt-1 font-head text-[17px] font-bold text-slate-900">{o.title}</h3>
                  {o.amount ? <div className="mt-1 text-[13px] font-semibold text-slate-700">{o.amount}</div> : null}
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-500">{o.summary}</p>
                  {o.best_for ? <p className="mt-3 text-[12px] text-slate-600"><span className="font-semibold text-slate-800">Best for:</span> {o.best_for}</p> : null}
                  {o.requirements?.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {o.requirements.map((r) => (
                        <li key={r} className="flex items-start gap-2 text-[12px] text-slate-600"><Check size={13} className="mt-0.5 shrink-0" style={{ color: meta.accent }} />{r}</li>
                      ))}
                    </ul>
                  )}
                  {o.reports_to && o.reports_to !== "N/A" ? (
                    <div className="mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-3 font-mono text-[10px] text-slate-400"><ShieldCheck size={12} /> Reports to {o.reports_to}</div>
                  ) : null}
                  <Link to={primaryTo} data-testid={`opp-cta-${o.slug}`} className="mt-4 flex items-center gap-1.5 text-[13px] font-semibold text-[#1a5fd0] transition-all hover:gap-2.5">Check my readiness <ArrowRight size={14} /></Link>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#0f172a] p-8 sm:flex-row sm:items-center sm:justify-between" data-testid="opportunities-cta">
          <div>
            <div className="font-serif-display text-2xl font-medium text-white">See what you'd qualify for.</div>
            <p className="mt-1 text-[14px] text-slate-300">Your Funding Readiness Score maps you to the right capital paths — and tells you what to fix first.</p>
          </div>
          <Link to={primaryTo} data-testid="opportunities-cta-start" className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#b0862f] to-[#d8b25a] px-6 py-3 text-[13px] font-semibold text-[#0f172a] active:scale-95">Get funding-ready <ArrowRight size={15} /></Link>
        </div>
        <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-wider text-slate-400">Educational directory · Not an offer, guarantee of approval, or credit application</p>
      </div>
      <PublicFooter />
    </div>
  );
}
